import {
  allAttachmentsKey,
  allEmailsKey,
  allFormsKey,
  allHistoriesKey,
  allIntegrationsKey,
  allMsgsKey,
  allOrdersKey,
  allQuestionsKey,
  allTicketsKey,
  environmentIsDevelopment,
  messageSubChannel,
  ss,
  voiceSubChannel,
} from "consts";
import { AttendanceModel, BaseProviderType } from "models";
import {
  EmailProvider,
  FormFieldProvider,
  HistoricProvider,
  IntegrationProvider,
  MessageProvider,
  OrderProvider,
  QuestionProvider,
  TicketProvider,
  useAttendList,
  useProfile,
  useSoftphone,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { AttachmentProvider } from "./attachment";

type AttendDetailContextType = {
  pIdOmni: string;
  attendance?: AttendanceModel;
  isMessage?: boolean;
  isVoice?: boolean;
  clearAttendanceData: (idOmni: string) => void;
};

type AttendDetailProviderType = BaseProviderType & {
  pIdOmni: string;
};

export type AttBaseProviderType = BaseProviderType & {
  all: any;
};

export type PermissionAttProviderType = AttBaseProviderType & {
  hasPermission?: boolean;
};

const AttendDetailContext = createContext({} as AttendDetailContextType);

const allAttachments = JSON.parse(ss.getItem(allAttachmentsKey) ?? "{}");
const allEmails = JSON.parse(ss.getItem(allEmailsKey) ?? "{}");
const allForms = JSON.parse(ss.getItem(allFormsKey) ?? "{}");
const allIntegrations = JSON.parse(ss.getItem(allIntegrationsKey) ?? "{}");
const allHistories = JSON.parse(ss.getItem(allHistoriesKey) ?? "{}");
const allMessages = JSON.parse(ss.getItem(allMsgsKey) ?? "{}");
const allOrders = JSON.parse(ss.getItem(allOrdersKey) ?? "{}");
const allQuestions = JSON.parse(ss.getItem(allQuestionsKey) ?? "{}");
const allTickets = JSON.parse(ss.getItem(allTicketsKey) ?? "{}");

export const AttendDetailProvider = ({
  children,
  pIdOmni,
}: AttendDetailProviderType) => {
  const { user } = useProfile();
  const { resetSoftphoneActions } = useSoftphone();
  const { getAttendance } = useAttendList();

  const attendance = useMemo(
    () => getAttendance(Number(pIdOmni)),
    [pIdOmni, getAttendance],
  );

  const isMessage = useMemo(
    () => attendance && messageSubChannel.includes(attendance.IdSubChannelType),
    [attendance],
  );

  const isVoice = useMemo(
    () => attendance && voiceSubChannel.includes(attendance.IdSubChannelType),
    [attendance],
  );

  useEffect(() => {
    if (pIdOmni) resetSoftphoneActions();
  }, [pIdOmni, resetSoftphoneActions]);

  const clearAttendanceData = useCallback((idOmni: string) => {
    delete allAttachments[idOmni];
    ss.setItem(allAttachmentsKey, JSON.stringify(allAttachments));

    delete allEmails[idOmni];
    ss.setItem(allEmailsKey, JSON.stringify(allEmails));

    delete allForms[idOmni];
    ss.setItem(allFormsKey, JSON.stringify(allForms));

    delete allIntegrations[idOmni];
    ss.setItem(allIntegrationsKey, JSON.stringify(allIntegrations));

    delete allHistories[idOmni];
    ss.setItem(allHistoriesKey, JSON.stringify(allHistories));

    delete allMessages[idOmni];
    ss.setItem(allMsgsKey, JSON.stringify(allMessages));

    delete allOrders[idOmni];
    ss.setItem(allOrdersKey, JSON.stringify(allOrders));

    delete allQuestions[idOmni];
    ss.setItem(allQuestionsKey, JSON.stringify(allQuestions));

    delete allTickets[idOmni];
    ss.setItem(allTicketsKey, JSON.stringify(allTickets));
  }, []);

  return (
    <AttendDetailContext.Provider
      value={{ pIdOmni, attendance, isMessage, isVoice, clearAttendanceData }}
    >
      <MessageProvider all={allMessages} hasPermission={isMessage}>
        <IntegrationProvider all={allIntegrations} hasPermission={isVoice}>
          <TicketProvider
            all={allTickets}
            hasPermission={user?.hasTicketModule}
          >
            <OrderProvider all={allOrders} hasPermission={user?.hasOrderModule}>
              <QuestionProvider
                all={allQuestions}
                hasPermission={user?.hasQuestionnaireModule}
              >
                <FormFieldProvider all={allForms}>
                  <HistoricProvider
                    all={allHistories}
                    hasPermission={!user?.mustHideTimeline}
                  >
                    <EmailProvider
                      all={allEmails}
                      hasPermission={environmentIsDevelopment}
                    >
                      <AttachmentProvider
                        all={allAttachments}
                        hasPermission={user?.hasMailingAttachment}
                      >
                        {children}
                      </AttachmentProvider>
                    </EmailProvider>
                  </HistoricProvider>
                </FormFieldProvider>
              </QuestionProvider>
            </OrderProvider>
          </TicketProvider>
        </IntegrationProvider>
      </MessageProvider>
    </AttendDetailContext.Provider>
  );
};

export const useAttendDetail = () => useContext(AttendDetailContext);
